// Ground under and around the circuit. The track meshes carry their own
// surfaces (road, run-off, grass, gravel); the terrain fills in everything
// else and must never rise above them, or it pokes through the track.
//
// Near the circuit the terrain is a grid whose heights are the LOWEST track
// elevation within reach of each grid point, minus a margin, so interpolating
// across a cell can't climb above any nearby track surface. Further out, a
// large flat skirt below everything runs into the haze.

import { Color3, Mesh, MeshBuilder, Scene, StandardMaterial, VertexData } from './babylon';
import type { Track } from '../track/track';

/** How far below the lowest nearby track surface the terrain sits, metres. */
const BELOW_TRACK = 0.6;
/** Grid spacing near the circuit, metres. */
const CELL = 25;
/** Extra grid beyond the circuit's bounding box, metres. */
const BORDER = 300;

export interface Terrain {
  meshes: Mesh[];
  /** Ground height at a world position (on the terrain surface). */
  groundAt(x: number, z: number): number;
  /** Lowest terrain height anywhere. */
  floor: number;
}

export function buildTerrain(scene: Scene, track: Track, color = new Color3(0.19, 0.33, 0.15)): Terrain {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  let lowest = Infinity;
  for (let i = 0; i < track.count; i++) {
    const p = track.points[i]!;
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
    lowest = Math.min(lowest, track.elevation?.[i] ?? 0);
  }
  const floor = lowest - BELOW_TRACK - 1.5;
  const x0 = minX - BORDER;
  const z0 = minZ - BORDER;
  const nx = Math.ceil((maxX - minX + 2 * BORDER) / CELL);
  const nz = Math.ceil((maxZ - minZ + 2 * BORDER) / CELL);

  // Track points bucketed into cells for fast "lowest nearby" queries.
  const reach = CELL * 1.6;
  const bucket = new Map<number, number[]>();
  const key = (i: number, j: number): number => i * 100003 + j;
  for (let k = 0; k < track.count; k++) {
    const p = track.points[k]!;
    const i = Math.floor((p.x - x0) / CELL);
    const j = Math.floor((p.z - z0) / CELL);
    const list = bucket.get(key(i, j)) ?? [];
    list.push(k);
    bucket.set(key(i, j), list);
  }
  // Track extends this far either side of the centreline (road + run-off + grass + verge).
  const halfWidth = 45;
  const heights = new Float32Array((nx + 1) * (nz + 1));
  for (let j = 0; j <= nz; j++) {
    for (let i = 0; i <= nx; i++) {
      const x = x0 + i * CELL;
      const z = z0 + j * CELL;
      let h = Infinity;
      const span = Math.ceil((reach + halfWidth) / CELL);
      for (let di = -span; di <= span; di++) {
        for (let dj = -span; dj <= span; dj++) {
          for (const k of bucket.get(key(i + di, j + dj)) ?? []) {
            const p = track.points[k]!;
            if (Math.hypot(p.x - x, p.z - z) <= reach + halfWidth) h = Math.min(h, track.elevation?.[k] ?? 0);
          }
        }
      }
      if (h === Infinity) {
        // Away from the circuit: follow the nearest track height, eased down towards the border.
        h = track.heightNear(x, z).y;
      }
      // Fade to the skirt height over the outer cells so the edge is seamless.
      const edge = Math.min(i, j, nx - i, nz - j);
      const fade = Math.min(1, edge / 4);
      heights[j * (nx + 1) + i] = floor + (h - BELOW_TRACK - floor) * fade;
    }
  }

  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let j = 0; j <= nz; j++) {
    for (let i = 0; i <= nx; i++) {
      positions.push(x0 + i * CELL, heights[j * (nx + 1) + i]!, z0 + j * CELL);
      uvs.push(i * CELL, j * CELL);
    }
  }
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const a = j * (nx + 1) + i;
      const b = a + 1;
      const c = a + nx + 1;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }
  const vd = new VertexData();
  vd.positions = positions;
  vd.uvs = uvs;
  vd.indices = indices;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  // Make sure the normals face up (depends on the winding convention).
  if (normals[1]! < 0) for (let k = 0; k < normals.length; k++) normals[k] = -normals[k]!;
  vd.normals = normals;
  const grid = new Mesh('terrain', scene);
  vd.applyToMesh(grid);

  const mat = new StandardMaterial('terrain-mat', scene);
  mat.diffuseColor = color;
  mat.specularColor = Color3.Black();
  mat.backFaceCulling = false;
  grid.material = mat;
  grid.isPickable = false;
  grid.freezeWorldMatrix();

  // Flat skirt to the horizon, just below the grid's faded edge.
  const skirt = MeshBuilder.CreateGround('terrain-skirt', { width: 8000, height: 8000 }, scene);
  skirt.position.set((minX + maxX) / 2, floor - 0.3, (minZ + maxZ) / 2);
  skirt.material = mat;
  skirt.isPickable = false;
  skirt.freezeWorldMatrix();

  const groundAt = (x: number, z: number): number => {
    const fx = (x - x0) / CELL;
    const fz = (z - z0) / CELL;
    const i = Math.floor(fx);
    const j = Math.floor(fz);
    if (i < 0 || j < 0 || i >= nx || j >= nz) return floor;
    const u = fx - i;
    const v = fz - j;
    const h = (a: number, b: number): number => heights[b * (nx + 1) + a]!;
    // Matches the triangle split used above (a-c-b, b-c-d).
    if (u + v <= 1) return h(i, j) + (h(i + 1, j) - h(i, j)) * u + (h(i, j + 1) - h(i, j)) * v;
    return h(i + 1, j + 1) + (h(i, j + 1) - h(i + 1, j + 1)) * (1 - u) + (h(i + 1, j) - h(i + 1, j + 1)) * (1 - v);
  };
  return { meshes: [grid, skirt], groundAt, floor };
}
